import { useEffect, useState } from 'react'
import { AppButton } from '@clickguard/design-system'
import { VerificationController } from 'components/inactive-tracking-code/verification-controller'
import { useTrackPostHogEvent } from 'utils/posthog'
import { PostHogEvent, PostHogPath } from 'utils/posthog-events'

interface InactiveTrackingCodeBannerProps {
	websiteName?: string
	accountId?: string
	assetId?: string
}

const AlertIcon = () => (
	<svg
		width="30"
		height="31"
		viewBox="0 0 30 31"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
	>
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M21.3621 6.17914e-07H8.6382C7.43075 -1.79368e-05 6.43419 -3.32482e-05 5.62241 0.0662924C4.77926 0.13518 4.00421 0.283027 3.2762 0.653963C2.14723 1.2292 1.22935 2.14709 0.654106 3.27606C0.283169 4.00406 0.135323 4.77912 0.0664351 5.62226C0.000109093 6.43406 0.000124827 7.4306 0.00014396 8.63805L7.0825e-05 18.2064C-0.00057523 19.3994 -0.00105498 20.2854 0.20459 21.0529C0.759392 23.1235 2.37668 24.7408 4.44723 25.2956C4.90843 25.4191 5.41243 25.4683 6.00014 25.4877L6.00014 27.5564C6.00007 27.9093 6 28.269 6.02677 28.5638C6.05238 28.8457 6.12019 29.3778 6.49167 29.8432C6.91915 30.3787 7.56736 30.6903 8.25257 30.6895C8.84805 30.6889 9.30591 30.4094 9.54205 30.2533C9.78896 30.0901 10.0698 29.8653 10.3453 29.6448L13.965 26.749C14.7429 26.1267 14.9738 25.9511 15.2138 25.8285C15.4545 25.7055 15.7107 25.6156 15.9755 25.5613C16.2395 25.5071 16.5296 25.5 17.5258 25.5H21.3621C22.5696 25.5 23.5661 25.5 24.3779 25.4337C25.221 25.3648 25.9961 25.217 26.7241 24.846C27.8531 24.2708 28.7709 23.3529 29.3462 22.2239C29.7171 21.4959 29.865 20.7209 29.9339 19.8777C30.0002 19.066 30.0002 18.0694 30.0001 16.862V8.63802C30.0002 7.43058 30.0002 6.43405 29.9339 5.62226C29.865 4.77912 29.7171 4.00406 29.3462 3.27606C28.7709 2.14709 27.8531 1.2292 26.7241 0.653963C25.9961 0.283027 25.221 0.13518 24.3779 0.0662924C23.5661 -3.32482e-05 22.5695 -1.79368e-05 21.3621 6.17914e-07ZM16.5 7.5C16.5 6.67157 15.8284 6 15 6C14.1716 6 13.5 6.67157 13.5 7.5V12.75C13.5 13.5784 14.1716 14.25 15 14.25C15.8284 14.25 16.5 13.5784 16.5 12.75V7.5ZM15 16.5C14.1716 16.5 13.5 17.1716 13.5 18C13.5 18.8284 14.1716 19.5 15 19.5H15.015C15.8434 19.5 16.515 18.8284 16.515 18C16.515 17.1716 15.8434 16.5 15.015 16.5H15Z"
			fill="#BB241A"
		/>
	</svg>
)

export const InactiveTrackingCodeBanner = ({
	websiteName,
	accountId,
	assetId
}: InactiveTrackingCodeBannerProps) => {
	const displayName = websiteName || 'your website'
	const [isModalOpen, setIsModalOpen] = useState(false)
	const trackPostHogEvent = useTrackPostHogEvent()

	// Track banner impression on mount
	useEffect(() => {
		trackPostHogEvent(
			PostHogEvent.INACTIVE_TC_BANNER_SHOWN,
			PostHogPath.DASHBOARD,
			{ website_name: websiteName }
		)
	}, [])

	return (
		<>
			<div className="w-full flex items-center gap-4">
				{/* Alert Icon */}
				<div className="flex-shrink-0 flex items-center justify-center w-[36px] h-[36px]">
					<AlertIcon />
				</div>

				{/* Text Content */}
				<div className="flex-1 min-w-0">
					<p className="text-lg font-medium leading-[30px]">
						<span className="text-[#BB241A]">Action required</span>
						<span className="text-t-default">
							: tracking code not detected on {displayName}
						</span>
					</p>
					<p className="text-base font-normal text-t-default leading-[30px]">
						Take a moment to verify the installation and restore full
						protection — or talk to our support team, we&apos;re here to
						help!
					</p>
				</div>

				{/* CTA Button */}
				<div className="flex-shrink-0">
					<AppButton
						size="md"
						label="Start verification"
						onClick={() => {
						trackPostHogEvent(
							PostHogEvent.INACTIVE_TC_VERIFICATION_STARTED,
							PostHogPath.DASHBOARD,
							{ website_name: websiteName }
						)
						setIsModalOpen(true)
					}}
					/>
				</div>
			</div>

			{/* Verification Modal */}
			<VerificationController
				open={isModalOpen}
				onClose={() => setIsModalOpen(false)}
				accountId={accountId || ''}
				assetId={assetId || ''}
			/>
		</>
	)
}
